import { IonChip, IonIcon, IonLabel, IonSkeletonText, useIonModal } from '@ionic/react';
import { modalController } from '@ionic/core';
import styles from './EventClassificationChip.module.css';
import { checkmark, chevronExpand, chevronForwardOutline } from 'ionicons/icons';
import { useDeviceEventQuery } from '../query/deviceEventQuery';
import { useEventUserFeedbackQuery } from '../query/eventUserFeedbackQuery';
import { FeedbackResolution } from '../models/EventUserFeedback';
import { useComponentTranslation } from '../hooks/useTranslation';
import { colorForEventClassification, iconForEventClassification, labelForEventClassification } from '../utils/eventClassificationUtils';
import DetectionFeedbackModal from '../modals/DetectionFeedbackModal';

interface EventClassificationChipProps {
    deviceId: string;
    eventId: number;
    accessToken?: string | null;
}

const EventClassificationChip: React.FC<EventClassificationChipProps> = ({ deviceId, eventId, accessToken }) => {
    const { t } = useComponentTranslation();
    const eventQuery = useDeviceEventQuery(deviceId, eventId, accessToken);
    const feedbackQuery = useEventUserFeedbackQuery(deviceId, eventId);
    const event = eventQuery.data;
    const feedback = feedbackQuery.data;
    const classificationChanged = event?.hasManualClassification && (event?.eventManualClassification != event?.eventClassification);
    const feedbackReviewed = !!feedback && feedback.feedbackResolution != null && feedback.feedbackResolution !== FeedbackResolution.Unresolved;

    const [presentFeedbackModal, dismissFeedbackModal] = useIonModal(DetectionFeedbackModal, {
        deviceId,
        eventId,
        accessToken,
        onDismiss: () => dismissFeedbackModal()
    });

    const openFeedbackModal = async () => {
        presentFeedbackModal({
            // Five options overflow Ionic's default 600x600 wide-screen dialog.
            cssClass: 'oc-dialog-m',
            presentingElement: await modalController.getTop() || document.getElementsByTagName('ion-router-outlet')[0]
        });
    };

    if (!event) return <IonChip><IonSkeletonText style={{ width: '50px' }}></IonSkeletonText></IonChip>;

    return (
        <>
            <IonChip color={colorForEventClassification(event.eventEffectiveClassification)} className={classificationChanged ? styles.correctedChip : undefined} onClick={openFeedbackModal} >
                {!classificationChanged && <>
                    <IonIcon icon={iconForEventClassification(event.eventEffectiveClassification)} />
                    <IonLabel>{labelForEventClassification(event.eventClassification, t)}</IonLabel>
                    {event.hasManualClassification && <IonIcon icon={checkmark} />}
                </>}
                {/* original over corrected: side by side the pair crushes the
                    event title out of the row on phone widths */}
                {classificationChanged && <div className={styles.correctionStack}>
                    <span className={styles.correctionRow}>
                        <IonIcon icon={iconForEventClassification(event.eventEffectiveClassification)} />
                        <IonLabel>{labelForEventClassification(event.eventClassification, t)}</IonLabel>
                    </span>
                    <span className={styles.correctionRow}>
                        <IonIcon icon={chevronForwardOutline} />
                        <IonLabel>{labelForEventClassification(event.eventManualClassification, t)}</IonLabel>
                    </span>
                </div>}
                {feedback && <>
                    {!feedbackReviewed && <span className={styles.inReview}>{t('event_classification_chip.feedback.status_in_review')}</span>}
                    {feedbackReviewed && <span className={styles.reviewed}>{t('event_classification_chip.feedback.status_reviewed')}</span>}
                </>}
                {(!feedback || feedback.feedbackResolution === FeedbackResolution.Unresolved) && <IonIcon icon={chevronExpand} />}
            </IonChip>
        </>
    );
};

export default EventClassificationChip;
